import React, { useRef, useEffect, useCallback, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const CorrectionCanvas = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);
  const [dimensions, setDimensions] = useState({ w: 800, h: 400 });

  useEffect(() => {
    const updateSize = () => {
      const container = canvasRef.current?.parentElement;
      if (container) {
        setDimensions({ w: container.offsetWidth, h: Math.min(400, container.offsetWidth * 0.5) });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    timeRef.current += 0.004;
    const t = timeRef.current;

    ctx.clearRect(0, 0, w, h);

    const numLines = 5;
    const correctionFactor = Math.min(1, Math.max(0, (Math.sin(t * 0.5) + 1) / 2));

    for (let lineIdx = 0; lineIdx < numLines; lineIdx++) {
      const baseY = h * 0.2 + (lineIdx * h * 0.15);
      const points = [];

      for (let x = 0; x <= w; x += 3) {
        // Messy/human version
        const messyY = baseY
          + Math.sin(x * 0.02 + lineIdx * 2 + t * 0.3) * 25
          + Math.sin(x * 0.05 + lineIdx + t * 0.7) * 12
          + Math.sin(x * 0.1 + t) * 5
          + (Math.random() - 0.5) * 3;

        // Clean/AI version  
        const cleanY = baseY + Math.sin(x * 0.01 + lineIdx * 1.5) * 8;

        // Interpolate between messy and clean based on x position
        const xProgress = x / w;
        const localCorrection = Math.max(0, Math.min(1, (xProgress - 0.1) / 0.6));
        const blendedCorrection = localCorrection * correctionFactor;

        const finalY = messyY * (1 - blendedCorrection) + cleanY * blendedCorrection;
        points.push({ x, y: finalY });
      }

      // Draw messy side glow
      ctx.beginPath();
      points.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
      const gradient = ctx.createLinearGradient(0, 0, w, 0);
      gradient.addColorStop(0, `rgba(224, 90, 71, ${0.4 - lineIdx * 0.06})`);
      gradient.addColorStop(0.4, `rgba(212, 163, 115, ${0.3 - lineIdx * 0.04})`);
      gradient.addColorStop(0.7, `rgba(0, 255, 148, ${0.3 - lineIdx * 0.04})`);
      gradient.addColorStop(1, `rgba(0, 240, 255, ${0.4 - lineIdx * 0.06})`);
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Processing arrow / indicator at the transition point
    const arrowX = w * (0.3 + correctionFactor * 0.3);
    ctx.save();
    ctx.strokeStyle = `rgba(0, 240, 255, ${0.2 + Math.sin(t * 2) * 0.1})`;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(arrowX, 0);
    ctx.lineTo(arrowX, h);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Labels
    ctx.font = '12px "JetBrains Mono", monospace';
    ctx.fillStyle = 'rgba(224, 90, 71, 0.8)';
    ctx.fillText('human input', 10, 20);
    ctx.fillStyle = 'rgba(0, 240, 255, 0.8)';
    ctx.textAlign = 'right';
    ctx.fillText('refined output', w - 10, 20);
    ctx.textAlign = 'left';

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = dimensions.w;
    canvas.height = dimensions.h;
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [dimensions, draw]);

  return <canvas ref={canvasRef} className="w-full" style={{ height: dimensions.h }} />;
};

export const PhilosophySection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      id="philosophy"
      ref={sectionRef}
      data-testid="philosophy-section"
      className="relative py-24 md:py-36 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #050A10 0%, #0A0808 50%, #050A10 100%)' }}
    >
      {/* Background subtle texture */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="text-ai-text/50 text-xs uppercase tracking-[0.4em] block mb-4">{'// 01'}</span>
          <h2 className="text-2xl md:text-4xl font-light leading-tight">
            <span className="italic text-human-text">The problem</span>
            {' '}
            <span className="text-ai-primary font-semibold uppercase tracking-wider">& the fix</span>
          </h2>
        </motion.div>

        {/* Canvas visualization */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16 border border-ai-secondary/20 rounded-sm p-4 bg-ai-bg/50 backdrop-blur-sm"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-human-primary/60" />
            <div className="w-2 h-2 rounded-full bg-human-secondary/40" />
            <div className="w-2 h-2 rounded-full bg-order/40" />
            <span className="text-ai-text/50 text-xs uppercase tracking-widest ml-2">correction_engine.render()</span>
          </div>
          <CorrectionCanvas />
        </motion.div>

        {/* Philosophy text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-12 gap-8"
        >
          <div className="md:col-span-7">
            <p className="text-human-text/90 text-base md:text-lg leading-[1.9] font-light italic">
              "AI is fast, but it hallucinates. It creates <span className="text-chaos">'spaghetti code'</span> at
              lightning speed. Turinga Dev fixes this. I bring {new Date().getFullYear() - 2014}+ years of <span className="text-human-primary">'Old School'</span> engineering
              fundamentals—security, scalability, and cost-optimization—to guide the raw power of AI."
            </p>
          </div>
          <div className="md:col-span-5 md:border-l md:border-ai-secondary/20 md:pl-8">
            <div className="space-y-4">
              {[
                { label: 'SECURITY', icon: '[ ]', color: 'text-order' },
                { label: 'SCALABILITY', icon: '[ ]', color: 'text-ai-primary' },
                { label: 'COST-OPT', icon: '[ ]', color: 'text-human-primary' },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6 + i * 0.15, duration: 0.5 }}
                  className="flex items-center gap-3"
                >
                  <span className={`text-sm font-bold ${item.color}`}>{item.icon}</span>
                  <span className="text-ai-text/80 text-xs uppercase tracking-[0.3em] font-semibold">{item.label}</span>
                  <motion.div
                    className={`flex-1 h-px ${item.color === 'text-order' ? 'bg-order/30' : item.color === 'text-ai-primary' ? 'bg-ai-primary/30' : 'bg-human-primary/30'}`}
                    initial={{ scaleX: 0 }}
                    animate={isInView ? { scaleX: 1 } : {}}
                    transition={{ delay: 0.8 + i * 0.15, duration: 0.6 }}
                    style={{ transformOrigin: 'left' }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
